// common store for managing favourites on client and admin interfaces
import { createRESTStore } from "@stores/shared/createRESTStore";
import { createRerankAction } from "@stores/shared/createRerankAction";
import API from "@config/shared/api-paths";
import apiClient from "@services/api/apiClient";

// fetch endpoint for client has been completely removed
// there's no need of fetching full list of favourites anywhwere other than the tree
// nothing will be affected if admin ednpoint is also removed

const useFavouritesStore = createRESTStore({
  entityName: "favourites",
  apiPaths: {
    fetch: "",
    create: "",
    update: "",
    delete: "",
  },
  initialState: [],

  processResponse: {
    fetch: (data) => data,
    create: (data) => data,
    update: (data) => data,
    delete: (data) => data,
  },

  additionalActions: (set, get) => {
    // Shared rerank action — the favourite collection scope is required by the
    // backend, so it's routed through extraParams. Local state is synced by the
    // drop hook (via the returned data), not here, so no applyResult.
    const rerankFavouriteAction = createRerankAction({
      getApiPaths: () => get().apiPaths,
    });

    return {
      initializeFavourites: (interfaceType) => {
        const newApiPaths = {
          fetch: API.BASE + API[interfaceType.toUpperCase()].FAVOURITE,
          create: API.BASE + API[interfaceType.toUpperCase()].FAVOURITE,
          update: API.BASE + API[interfaceType.toUpperCase()].FAVOURITE,
          delete: API.BASE + API[interfaceType.toUpperCase()].FAVOURITE,
        };
        set((state) => ({
          ...state,
          currentInterfaceType: interfaceType,
          apiPaths: newApiPaths,
        }));
      },

      createFavourite: async (
        data,
        successCallback,
        errorCallback,
        afterCreate,
      ) => {
        set({ creating: true });
        try {
          const result = await apiClient.post(get().apiPaths.create, data);
          set({ creating: false });
          successCallback();
          afterCreate?.(result.data || result);
        } catch (error) {
          errorCallback(error);
          set({ creating: false });
        }
      },

      updateFavourite: async (
        id,
        data,
        successCallback,
        errorCallback,
        afterUpdate,
      ) => {
        set({ updating: true });
        try {
          const result = await apiClient.put(
            get().apiPaths.update + "/" + id,
            data,
          );
          set({ updating: false });
          successCallback();
          afterUpdate?.(result.data || result);
        } catch (error) {
          errorCallback(error);
          set({ updating: false });
        }
      },

      // Public signature preserved for callers; the URL contract is now built
      // by the shared createRerankAction factory. Local state is synced by the
      // caller (useFavouriteDrop) from the returned data.
      rerankFavourite: (
        id,
        predecessorRank,
        favouriteCollectionID,
        successCallback,
        errorCallback,
        afterRerank,
      ) => {
        set({ updating: true });
        return rerankFavouriteAction(
          id,
          predecessorRank,
          { favouriteCollectionID },
          {
            onSuccess: (data) => {
              set({ updating: false });
              successCallback?.();
              afterRerank?.(data);
            },
            onError: (error) => {
              set({ updating: false });
              errorCallback?.(error);
            },
          },
        );
      },

      deleteFavourite: async (
        id,
        collectionId,
        successCallback,
        errorCallback,
        afterDelete,
      ) => {
        set({ deleting: true });
        try {
          await apiClient.delete(get().apiPaths.delete + "/" + id);
          set({ deleting: false });
          successCallback();
          afterDelete?.(id, collectionId);
        } catch (error) {
          console.error("Delete error:", error);
          set({ deleting: false });
          errorCallback(error);
        }
      },

      checkIsFavourite: async (url) => {
        try {
          const result = await apiClient.post(
            `${API.BASE + API[get().currentInterfaceType.toUpperCase()].FAVOURITE}/isfavourite`,
            { url },
          );
          return result.data ?? result;
        } catch (error) {
          console.error("Error while checking favourite:", error);
          return false;
        }
      },
    };
  },
});

export default useFavouritesStore;
